/**
 * 把 useFlowState 持有的 allNodes/allEdges 序列化成前端对外暴露的
 * FlowGraph JSON 契约（与 hogwarts_dash_reactflow.schema.py 的 FlowGraph/NodeData 一一对应）。
 *
 * 与 React Flow state 的关键差异：
 *   - React Flow 的节点会带 selected/dragging 等瞬时 UI 字段，必须剥掉，避免污染 JSON
 *   - 不剥掉会让"纯选中节点"操作触发 graph 重写、saveTrigger 抖动
 *
 * 邻接字段（spec 2026-07-24）：
 *   - 每个节点附加 prev_ids / next_ids 两数组
 *   - 空数组 = 起点 / 终点；非空 = 对应上游 / 下游 id 列表，支持一对多分叉与合并
 *   - edges 仍作为单源真理保留，prev_ids/next_ids 是「读视图冗余」
 *
 * @param {Array} allNodes  useFlowState().allNodes
 * @param {Array} allEdges  useFlowState().allEdges
 * @param {Object} [schema] FlowSchema（camelCase），用于给每个节点带出其 action 的 components
 * @returns {{
 *   schema_version: string,
 *   nodes: Array<{
 *     id: string, phase: string, action: string,
 *     position: {x: number, y: number},
 *     componentData: object | string,
 *     components: Array<Object>,

 *     prev_ids: string[], next_ids: string[]
 *   }>,
 *   edges: Array<{id?: string, source: string, target: string, animated?: boolean}>
 * }}
 */
export function serializeGraph(allNodes, allEdges, schema) {
  // 1. 邻接表预投影：O(N + E)，每个目标/源节点各收集一遍
  const prevMap = new Map(); // nodeId -> 上游 ids
  const nextMap = new Map(); // nodeId -> 下游 ids
  for (const e of allEdges) {
    if (!prevMap.has(e.target)) prevMap.set(e.target, []);
    if (!nextMap.has(e.source)) nextMap.set(e.source, []);
    prevMap.get(e.target).push(e.source);
    nextMap.get(e.source).push(e.target);
  }

  // 2. 节点投影：只保留业务字段，按统一顺序固定（先业务字段后邻接字段）
  // valueType 字段仅在节点「真正携带有效值」时输出：
  //   - 既要排除 undefined（字段根本不存在）—— 单值组件外其它节点默认无此字段
  //   - 也要排除 null（旧 Python to_json 在 default 下写出 "valueType": null，
  //     reload 后 JS 节点对象的 valueType 会是 null；序列化时必须再次吞掉，
  //     否则脏数据会无限回写）
  const nodes = allNodes.map((n) => {
    const out = {
      id: n.id,
      phase: n.phase,
      action: n.action,
      position: n.position,
      componentData: n.componentData,
      // 该节点 action 的 components 定义，原样从 schema 带入
      components: schema?.actions?.[n.action]?.components ?? [],
    };
    if (n.valueType != null) {
      // != null 同时挡 undefined 与 null（与 lodash _.isSet / q.isSet 同义）
      out.valueType = n.valueType;
    }
    out.prev_ids = prevMap.get(n.id) ?? [];
    out.next_ids = nextMap.get(n.id) ?? [];
    return out;
  });

  // 3. 边投影：剥掉 React Flow 内部字段，保留四字段
  const edges = allEdges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: e.animated,
  }));

  return {
    schema_version: "1.0.0",
    nodes,
    edges,
  };
}
